import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UserProfile, useUsersAdmin } from "./useUsersAdmin";
import { CreateUserFromRequest } from "@/components/admin/CreateUserFromRequest";
import { XCircle, Loader2, UserX, Trash2, Ban, UserPlus } from "lucide-react";
import { useState } from "react";
import { format } from "date-fns";
import { AccessRequest } from "@/types";
import { UserProfileForm } from "./UserProfileForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface UserDialogProps {
  user: UserProfile | AccessRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isAccessRequest?: boolean;
  isNewUser?: boolean;
}

export function UserDialog({ user, open, onOpenChange, isAccessRequest = false, isNewUser = false }: UserDialogProps) {
  const { 
    handleChangeRole, 
    handleReject, 
    handleApproveUser, 
    handleBanUser,
    handleUnbanUser,
    handleDeleteUser,
    handleUpdateUserProfile,
    uploadFile,
    isUploading,
    handleRejectAccessRequest,
    handleCreateAdminUser
  } = useUsersAdmin();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBanConfirm, setShowBanConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  
  // New state for creating users
  const [newUserData, setNewUserData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    role: "customer",
    isApproved: true
  });
  
  // Handle data from either user profile or access request
  const firstName = 'firstName' in user ? user.firstName : user.first_name;
  const lastName = 'lastName' in user ? user.lastName : user.last_name;
  const email = user.email;
  const phone = user.phone;
  const companyName = 'companyName' in user ? user.companyName : user.company_name;
  const licenseNumber = 'licenseNumber' in user ? user.licenseNumber : user.license_number;
  const message = user.message;
  
  // Additional fields for user profiles
  const isApproved = 'isApproved' in user ? user.isApproved : false;
  const isBanned = 'isBanned' in user ? user.isBanned : false;
  const role = 'role' in user ? user.role : 'customer';
  const websiteUrl = 'websiteUrl' in user ? user.websiteUrl : null;
  const companyLogoUrl = 'companyLogoUrl' in user ? user.companyLogoUrl : null;
  
  // Format display dates if available
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), 'PPP');
    } catch (e) {
      return dateString;
    }
  };
  
  const createdAt = formatDate(user.createdAt);
  const licenseExpiry = 'licenseExpiry' in user && user.licenseExpiry ? formatDate(user.licenseExpiry) : "N/A";
  const registrationDate = 'registrationDate' in user && user.registrationDate ? formatDate(user.registrationDate) : "N/A";

  const handleRoleChange = async (userId: string, newRole: string) => {
    setIsProcessing(true);
    await handleChangeRole(userId, newRole);
    setIsProcessing(false);
  };

  const handleRevokeAccess = async (userId: string) => {
    setIsProcessing(true);
    await handleReject(userId);
    onOpenChange(false);
    setIsProcessing(false);
  };

  const handleApprove = async (userId: string) => {
    setIsProcessing(true);
    await handleApproveUser(userId);
    setIsProcessing(false);
  };

  const handleBan = async (userId: string) => {
    setIsProcessing(true);
    await handleBanUser(userId);
    setShowBanConfirm(false);
    setIsProcessing(false);
  };

  const handleUnban = async (userId: string) => {
    setIsProcessing(true);
    await handleUnbanUser(userId);
    setIsProcessing(false);
  };

  const handleDelete = async (userId: string) => {
    setIsProcessing(true);
    await handleDeleteUser(userId);
    setShowDeleteConfirm(false);
    onOpenChange(false);
    setIsProcessing(false);
  };

  const handleRejectRequest = async (requestId: string) => {
    setIsProcessing(true);
    const success = await handleRejectAccessRequest(requestId);
    if (success) {
      onOpenChange(false);
    }
    setShowRejectConfirm(false);
    setIsProcessing(false);
  };

  // New function to handle admin user creation
  const handleCreateUser = async () => {
    setIsProcessing(true);
    const success = await handleCreateAdminUser(newUserData);
    if (success) {
      onOpenChange(false);
    }
    setIsProcessing(false);
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return "U";
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`;
  };

  const userStatusBadge = () => {
    if (isAccessRequest) {
      return <Badge variant="secondary">Access Request</Badge>;
    }
    if ('isBanned' in user && user.isBanned) {
      return <Badge variant="destructive">Banned</Badge>;
    }
    if ('isApproved' in user && user.isApproved) {
      return <Badge variant="default">Approved</Badge>;
    }
    return <Badge variant="secondary">Pending</Badge>;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              {isNewUser ? (
                <span>Create New User</span>
              ) : (
                <>
                  <span>{isAccessRequest ? "Access Request" : "User Details"}</span>
                  {userStatusBadge()}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {isNewUser 
                ? "Create a new user with admin or customer role."
                : isAccessRequest 
                  ? "Review and approve this access request." 
                  : "View and manage user information."}
            </DialogDescription>
          </DialogHeader>

          {isNewUser ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input 
                    id="firstName" 
                    value={newUserData.firstName}
                    onChange={(e) => setNewUserData({...newUserData, firstName: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input 
                    id="lastName" 
                    value={newUserData.lastName}
                    onChange={(e) => setNewUserData({...newUserData, lastName: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input 
                  id="password" 
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select 
                  value={newUserData.role} 
                  onValueChange={(value) => setNewUserData({...newUserData, role: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                className="w-full"
                onClick={handleCreateUser}
                disabled={isProcessing || !newUserData.email || !newUserData.password}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating User...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create User
                  </>
                )}
              </Button>
            </div>
          ) : isAccessRequest ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">First Name</h3>
                  <p className="text-base">{firstName || "N/A"}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Last Name</h3>
                  <p className="text-base">{lastName || "N/A"}</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Email</h3>
                <p className="text-base">{email}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Phone</h3>
                <p className="text-base">{phone || "N/A"}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Company</h3>
                <p className="text-base">{companyName || "N/A"}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-muted-foreground">License Number</h3>
                <p className="text-base">{licenseNumber || "N/A"}</p>
              </div>

              {message && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Additional Message</h3>
                  <p className="text-base whitespace-pre-wrap">{message}</p>
                </div>
              )}
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Created</h3>
                <p className="text-base">{createdAt}</p>
              </div>
              
              <div className="flex gap-2 mt-4">
                <CreateUserFromRequest
                  accessRequest={user as AccessRequest}
                  onSuccess={() => onOpenChange(false)}
                />
                
                <Button 
                  variant="destructive" 
                  className="w-full"
                  onClick={() => setShowRejectConfirm(true)}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject Request
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="edit">Edit Profile</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4 py-4">
                <div className="flex items-center space-x-4">
                  <Avatar className="h-16 w-16">
                    {companyLogoUrl ? (
                      <AvatarImage src={companyLogoUrl} alt={companyName || ""} />
                    ) : null}
                    <AvatarFallback className="text-lg">
                      {getInitials(firstName as string, lastName as string)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="text-xl font-semibold">{`${firstName || ""} ${lastName || ""}`}</h2>
                    <p className="text-muted-foreground">{email}</p>
                    {websiteUrl && (
                      <a 
                        href={websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        {websiteUrl}
                      </a>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Company</h3>
                    <p className="text-base">{companyName || "N/A"}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Phone</h3>
                    <p className="text-base">{phone || "N/A"}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">License Number</h3>
                    <p className="text-base">{licenseNumber || "N/A"}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">License Expiry</h3>
                    <p className="text-base">{licenseExpiry}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Registered</h3>
                    <p className="text-base">{registrationDate}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Created</h3>
                    <p className="text-base">{createdAt}</p>
                  </div>
                </div>

                {message && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Additional Message</h3>
                    <p className="text-base whitespace-pre-wrap">{message}</p>
                  </div>
                )}
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Role</h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button
                      variant={role === "admin" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleRoleChange((user as UserProfile).id, "admin")}
                      disabled={isProcessing}
                    >
                      {isProcessing && role !== "admin" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                      Set Admin
                    </Button>
                    <Button
                      variant={role === "customer" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleRoleChange((user as UserProfile).id, "customer")}
                      disabled={isProcessing}
                    >
                      {isProcessing && role !== "customer" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                      Set Customer
                    </Button>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Access Status</h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {!isApproved && 'id' in user ? (
                      <Button
                        variant="default"
                        className="flex items-center gap-1"
                        onClick={() => handleApprove(user.id)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                        Approve Access
                      </Button>
                    ) : 'id' in user ? (
                      <Button
                        variant="destructive"
                        className="flex items-center gap-1"
                        onClick={() => handleRevokeAccess(user.id)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <XCircle className="h-4 w-4 mr-1" />}
                        Revoke Access
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="flex justify-between mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveTab("edit")}
                  >
                    Edit Profile
                  </Button>
                  
                  <div className="space-x-2">
                    {('isBanned' in user && user.isBanned === true) ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnban((user as UserProfile).id)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                        Unban User
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive border-destructive hover:bg-destructive/10"
                        onClick={() => setShowBanConfirm(true)}
                        disabled={isProcessing}
                      >
                        <Ban className="h-4 w-4 mr-1" />
                        Ban User
                      </Button>
                    )}
                    
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isProcessing}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="edit" className="py-4">
                {'id' in user && (
                  <UserProfileForm 
                    user={user as UserProfile}
                    onUpdateProfile={(data) => handleUpdateUserProfile(user.id, data)}
                    onUploadFile={uploadFile}
                    isUploading={isUploading}
                  />
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user
              account and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleDelete((user as UserProfile).id)}
            >
              {isProcessing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showBanConfirm} onOpenChange={setShowBanConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ban this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will prevent the user from accessing the platform. You can unban them later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleBan((user as UserProfile).id)}
            >
              {isProcessing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Ban User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add the new confirmation dialog for rejecting access requests */}
      <AlertDialog open={showRejectConfirm} onOpenChange={setShowRejectConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this access request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reject the user's access request. They will not be able to access the platform.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleRejectRequest((user as AccessRequest).id)}
            >
              {isProcessing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Reject Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
